import React from 'react';
import {
  ChevronUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/20/solid';
import { cn } from 'utils/ui/classes';
import { ORDER, SortProvider, useSort } from './table/TableSorting';

export function Table({ children, className, dataTestId, ...props }) {
  return (
    (<table
      data-testid={dataTestId}
      className={cn('min-w-full divide-y divide-gray-300', className)}
      {...props}>
      {children}
    </table>)
  );
}

export function TableRow({ children, className, dataTestId, ...props }) {
  return (
    (<tr
      data-testid={dataTestId}
      className={cn(
        'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
        className,
      )}
      {...props}>
      {children}
    </tr>)
  );
}

export function TableHead({ children, className, dataTestId, ...props }) {
  return (
    (<th
      data-testid={dataTestId}
      className={cn(
        'px-3 py-3.5 text-left text-sm font-semibold text-gray-900',
        className,
      )}
      {...props}>
      {children}
    </th>)
  );
}

export function TableCell({ children, className, dataTestId, ...props }) {
  return (
    (<td
      data-testid={dataTestId}
      className={cn(
        'whitespace-nowrap px-3 py-4 text-sm text-gray-500 truncate',
        className,
      )}
      {...props}>
      {children}
    </td>)
  );
}

export function TableHeader({ children, className, onSortChange, dataTestId, ...props }) {
  return (
    (<SortProvider dataTestId="SortProvider-bdb9b0bb" onSortChange={onSortChange}>
      <thead
        data-testid={dataTestId}
        className={cn('bg-gray-50', className)}
        {...props}>
        {children}
      </thead>
    </SortProvider>)
  );
}

export function TableBody({ children, className, dataTestId, ...props }) {
  return (
    (<tbody
      data-testid={dataTestId}
      className={cn('bg-white', className)}
      {...props}>
      {children}
    </tbody>)
  );
}

export function TableFooter({ children, className, dataTestId, ...props }) {
  return (
    (<tfoot data-testid={dataTestId} className={cn(className)} {...props}>
      {children}
    </tfoot>)
  );
}

export function SortableColumnHeader({
  children,
  className,
  alignment,
  sortKey,
  dataTestId,
  ...props
}) {
  const {
    sortKey: currentSortKey,
    sortOrder: currentSortOrder,
    onSortChange,
  } = useSort();

  const isActiveSorting = currentSortKey === sortKey;

  return (
    (<TableHead dataTestId={dataTestId} className={className} {...props}>
      <button
        data-testid="button-74fd9e35"
        className={cn('flex items-center gap-1 w-full', {
          'justify-start': alignment === 'left',
          'justify-end': alignment === 'right',
          'justfy-center': alignment === 'center',
          'cursor-pointer': sortKey,
        })}
        disabled={!sortKey}
        onClick={() => sortKey && onSortChange(sortKey)}>
        <div data-testid="div-fd418439">{children}</div>

        {isActiveSorting &&
          (currentSortOrder === ORDER.ASC ? (
            <ArrowDownIcon className="flex-none h-4" />
          ) : (
            <ArrowUpIcon className="flex-none h-4" />
          ))}

        {!isActiveSorting && sortKey && (
          <ChevronUpDownIcon className="flex-none h-4 text-gray-400" />
        )}
      </button>
    </TableHead>)
  );
}
